import { Button } from "$components/button/button";
import { linkToPatientDetails } from "$routes/patient/[:patientId]/links";
import { CircularProgress, styled, Typography } from "@material-ui/core";
import { Error as ErrorIcon, LinkOffOutlined } from "@material-ui/icons";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { guardianRelationshipMap } from "./guardian-section";

export const LinkedGuardianDetails: FC<{
  details?: {
    patientId: string;
    fullName: string;
    phone: string;
    relationship: string;
  };
  areDetailsLoading: boolean;
  errorLoadingDetails: boolean;
  onClickUnlink: () => void;
}> = ({ areDetailsLoading, errorLoadingDetails, details, onClickUnlink }) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  if (areDetailsLoading) {
    return (
      <CantDisplayDetailsContainer>
        <CircularProgress />
      </CantDisplayDetailsContainer>
    );
  }

  if (errorLoadingDetails) {
    return (
      <CantDisplayDetailsContainer>
        <ErrorIcon color="primary" />
        <ErrorText>
          <FormattedMessage
            id="patient.linkedGuardian.errorLoadingDetails1"
            defaultMessage="This information is unavailable."
          />
          <br />
          <FormattedMessage
            id="patient.linkedGuardian.errorLoadingDetails2"
            defaultMessage="Please try again when you’re back online."
          />
        </ErrorText>
      </CantDisplayDetailsContainer>
    );
  }

  return (
    !!details && (
      <Container>
        <DetailsContainer>
          <Section>
            <Typography variant="body1">
              <FormattedMessage
                id="patient.linkedGuardian.linkedAccount"
                defaultMessage="Linked account"
              />
            </Typography>
            <LinkedAccountButton
              variant="text"
              color="primary"
              onClick={() =>
                navigate(
                  linkToPatientDetails({ patientUuid: details.patientId }),
                )
              }
            >
              {details.fullName}
            </LinkedAccountButton>
          </Section>
          <Section>
            <Typography variant="body1">
              <FormattedMessage
                id="patient.linkedGuardian.phone"
                defaultMessage="Phone number"
              />
            </Typography>
            <Value>{details.phone}</Value>
          </Section>
          <Section>
            <Typography variant="body1">
              <FormattedMessage
                id="patient.linkedGuardian.relationship"
                defaultMessage="Relationship"
              />
            </Typography>
            <Value>
              {formatMessage(guardianRelationshipMap[details.relationship])}
            </Value>
          </Section>
          <Section>
            <Typography variant="subtitle2">
              <FormattedMessage
                id="patient.linkedGuardian.footerText"
                defaultMessage="You can edit these details in the guardian’s account"
              />
            </Typography>
          </Section>
        </DetailsContainer>
        <Button
          variant="text"
          color="primary"
          startIcon={<LinkOffOutlined />}
          onClick={onClickUnlink}
        >
          <FormattedMessage
            id="patient.linkedGuardian.unlinkButton"
            defaultMessage="Unlink this guardian"
          />
        </Button>
      </Container>
    )
  );
};

const Container = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.spacing(2),
  marginBottom: "20px",
}));

const DetailsContainer = styled("div")(({ theme }) => ({
  width: "100%",
  borderRadius: "8px",
  backgroundColor: theme.palette.grey[100],
  padding: "20px",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "20px",
}));

const CantDisplayDetailsContainer = styled("div")(({ theme }) => ({
  width: "100%",
  borderRadius: "8px",
  backgroundColor: theme.palette.grey[100],
  padding: "90px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  marginBottom: "20px",
}));

const ErrorText = styled(Typography)(() => ({
  textAlign: "center",
}));

const Section = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "4px",
}));

const LinkedAccountButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: "none",
  padding: 0,
  height: "unset",
}));

const Value = styled(Typography)(() => ({
  fontWeight: 700,
}));
